import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useEmpresaId } from '../context/EmpresaContext';
import type { AppRol } from '../types/empacadora';

export interface AppPermisos {
  roles: AppRol[];
  perm_generar_boletas: boolean;
  perm_despachar: boolean;
  perm_confirmar_aplicacion: boolean;
  perm_reporte_campo: boolean;
  perm_vob: boolean;
  perm_ver_catalogo: boolean;
  perm_anular: boolean;
  perm_ver_reportes: boolean;
  perm_administrar: boolean;
  perm_muestrear: boolean;
  perm_sync_vfp: boolean;
  perm_boletas_labores: boolean;
  perm_cierre_planilla: boolean;
  loading: boolean;
}

const PERMS_VACIO: Omit<AppPermisos, 'roles' | 'loading'> = {
  perm_generar_boletas: false,
  perm_despachar: false,
  perm_confirmar_aplicacion: false,
  perm_reporte_campo: false,
  perm_vob: false,
  perm_ver_catalogo: false,
  perm_anular: false,
  perm_ver_reportes: false,
  perm_administrar: false,
  perm_muestrear: false,
  perm_sync_vfp: false,
  perm_boletas_labores: false,
  perm_cierre_planilla: false,
};

export function useAppPermisos(): AppPermisos {
  const empresaId = useEmpresaId();
  const [roles, setRoles]     = useState<AppRol[]>([]);
  const [perms, setPerms]     = useState(PERMS_VACIO);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function cargar() {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data } = await supabase
        .from('app_usuario_roles')
        .select('activo, rol:app_roles(*)')
        .eq('empresa_id', empresaId)
        .eq('user_id', user.id)
        .eq('activo', true);

      const rolesActivos: AppRol[] = (data ?? [])
        .map((r: any) => r.rol)
        .filter(Boolean);

      // union de todos los permisos (si tiene cualquier rol que lo permite → true)
      const merged = rolesActivos.reduce((acc, r) => ({
        perm_generar_boletas:     acc.perm_generar_boletas     || r.perm_generar_boletas,
        perm_despachar:           acc.perm_despachar           || r.perm_despachar,
        perm_confirmar_aplicacion:acc.perm_confirmar_aplicacion|| r.perm_confirmar_aplicacion,
        perm_reporte_campo:       acc.perm_reporte_campo       || r.perm_reporte_campo,
        perm_vob:                 acc.perm_vob                 || r.perm_vob,
        perm_ver_catalogo:        acc.perm_ver_catalogo        || r.perm_ver_catalogo,
        perm_anular:              acc.perm_anular              || r.perm_anular,
        perm_ver_reportes:        acc.perm_ver_reportes        || r.perm_ver_reportes,
        perm_administrar:         acc.perm_administrar         || r.perm_administrar,
        perm_muestrear:           acc.perm_muestrear           || r.perm_muestrear,
        perm_sync_vfp:            acc.perm_sync_vfp            || r.perm_sync_vfp,
        perm_boletas_labores:     acc.perm_boletas_labores     || !!r.perm_boletas_labores,
        perm_cierre_planilla:     acc.perm_cierre_planilla     || !!r.perm_cierre_planilla,
      }), { ...PERMS_VACIO });

      setRoles(rolesActivos);
      setPerms(merged);
      setLoading(false);
    }
    cargar();
  }, [empresaId]);

  return { roles, ...perms, loading };
}
